import React, { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import SampleSticker from './SampleSticker';
import { printLabelBase64 } from '../utils/printServer';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
`;

const Content = styled.div`
  background: var(--al-bg);
  padding: 28px;
  border-radius: 8px;
  width: 720px;
  max-width: 94%;
  max-height: 92vh;
  overflow-y: auto;

  h2 {
    color: white;
    font-family: 'Montserrat', sans-serif;
    font-size: 28px;
    margin: 0 0 8px;
    font-weight: 600;
    text-transform: uppercase;
  }
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.65);
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  margin: 0 0 24px;
  text-transform: none;
  line-height: 1.4;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 28px;
  align-items: start;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const FormSide = styled.div``;

const PreviewSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const PreviewFrame = styled.div`
  background: #111;
  border: 1px solid #333;
  padding: 12px;
  border-radius: 4px;
`;

const PreviewLabel = styled.div`
  color: rgba(255, 255, 255, 0.55);
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: white;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 13px;
`;

const Meta = styled.div`
  color: white;
  font-family: 'Montserrat', sans-serif;
  font-size: 15px;
  text-transform: uppercase;
  line-height: 1.35;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 4px;
  background: white;
  color: black;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #62e07d;
  }
`;

const Warn = styled.div`
  background: rgba(255, 180, 60, 0.15);
  border: 1px solid rgba(255, 180, 60, 0.45);
  color: #ffd48a;
  padding: 12px;
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  text-transform: none;
  line-height: 1.4;
  margin-bottom: 16px;
`;

const Link = styled.a`
  color: #62e07d;
  word-break: break-all;
`;

const Status = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  text-transform: none;
  min-height: 1.2em;
  margin-top: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 28px;
`;

const CancelButton = styled.button`
  background: #333;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 14px;

  &:hover {
    background: #444;
  }
`;

const PrintButton = styled.button`
  background: #62e07d;
  color: black;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 14px;

  &:hover:not(:disabled) {
    background: #50b366;
  }

  &:disabled {
    background: #666;
    color: #ccc;
    cursor: not-allowed;
  }
`;

function SampleStickerModal({ product, onClose }) {
  const [material, setMaterial] = useState('');
  const [printing, setPrinting] = useState(false);
  const [status, setStatus] = useState('');
  const [printJob, setPrintJob] = useState(null);

  const supplierUrl = String(product?.supplierUrl || '').trim();
  const canPrint = Boolean(supplierUrl) && !printing;

  const handlePdfGenerated = useCallback(
    async (pdfData) => {
      if (!pdfData) return;
      try {
        setStatus('Sending to DYMO…');
        await printLabelBase64(pdfData, { type: 'line', tray: 1 });
        setStatus('Printed.');
        setTimeout(() => {
          setPrinting(false);
          setPrintJob(null);
          setStatus('');
        }, 1200);
      } catch (error) {
        console.error('Sample sticker print error:', error);
        setStatus('');
        setPrinting(false);
        setPrintJob(null);
        alert(`Failed to print: ${error.message}`);
      }
    },
    []
  );

  const handlePrint = () => {
    if (!canPrint) return;
    setStatus('Generating sticker…');
    setPrinting(true);
    setPrintJob({
      product,
      material: material.trim(),
      key: `${product.id}-${Date.now()}`
    });
  };

  if (!product) return null;

  return (
    <Overlay onClick={onClose}>
      <Content onClick={(e) => e.stopPropagation()}>
        <h2>Sample Sticker</h2>
        <Subtitle>
          Prints on the same DYMO stock as line stickers. QR opens the supplier
          page for colors and inventory.
        </Subtitle>

        <Layout>
          <FormSide>
            <FormGroup>
              <Label>Brand</Label>
              <Meta>{product.brand || '—'}</Meta>
            </FormGroup>
            <FormGroup>
              <Label>Style</Label>
              <Meta>{product.styleNo || '—'}</Meta>
            </FormGroup>
            <FormGroup>
              <Label>Description</Label>
              <Meta>
                {product.shortDescription || product.description || '—'}
              </Meta>
            </FormGroup>
            <FormGroup>
              <Label>Material (optional)</Label>
              <Input
                value={material}
                onChange={(e) => setMaterial(e.target.value)}
                placeholder="e.g., 100% Cotton"
                disabled={printing}
              />
            </FormGroup>

            {supplierUrl ? (
              <FormGroup>
                <Label>QR Link</Label>
                <Meta style={{ textTransform: 'none', fontSize: 12 }}>
                  <Link href={supplierUrl} target="_blank" rel="noopener noreferrer">
                    {supplierUrl}
                  </Link>
                </Meta>
              </FormGroup>
            ) : (
              <Warn>
                This product has no supplier URL. Add one in Edit Product before
                printing a sample sticker.
              </Warn>
            )}
          </FormSide>

          <PreviewSide>
            <PreviewLabel>Preview</PreviewLabel>
            <PreviewFrame>
              <SampleSticker product={product} material={material} />
            </PreviewFrame>
          </PreviewSide>
        </Layout>

        <Status>{status}</Status>

        <ButtonGroup>
          <CancelButton onClick={onClose} disabled={printing}>
            Close
          </CancelButton>
          <PrintButton onClick={handlePrint} disabled={!canPrint}>
            {printing ? 'Printing…' : 'Print Sticker'}
          </PrintButton>
        </ButtonGroup>

        {printJob && (
          <div style={{ position: 'fixed', left: -9999, top: 0 }}>
            <SampleSticker
              key={printJob.key}
              product={printJob.product}
              material={printJob.material}
              autoGeneratePdf
              onPdfGenerated={handlePdfGenerated}
            />
          </div>
        )}
      </Content>
    </Overlay>
  );
}

export default SampleStickerModal;
